import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { AppState } from 'shared/store/reducers';
import { RejectError } from 'shared/api/axios';
import { fetchSidepanelPropertyGroups, patchSidepanelProperty } from 'shared/api';
import {
  SidepanelProperty,
  SidepanelPropertyGroup,
  SidepanelPropertyGroupType,
} from 'types/sidepanel';
import { Store } from 'shared/store/store';

export const fetchGroups = createAsyncThunk<
  SidepanelPropertyGroup[],
  void,
  { state: AppState; rejectValue: RejectError }
>('uiConfig/fetchGroups', async () => {
  return (await fetchSidepanelPropertyGroups()).data;
});

export const updateProperty = createAsyncThunk<
  { id: number } & Partial<SidepanelProperty>,
  { id: number } & Partial<SidepanelProperty>,
  { state: AppState; rejectValue: RejectError }
>('uiConfig/updateProperty', async data => {
  const { id, ...payload } = data;
  await patchSidepanelProperty(id, payload);
  return data;
});

export const initialState = {
  sidepanelPropertyGroups: [] as SidepanelPropertyGroup[],
};

// SLICE

const uiConfigSlice = createSlice({
  name: 'uiConfig',
  initialState,
  reducers: {
    reorderSidepanelProperties: (
      state,
      action: { payload: { type: SidepanelPropertyGroupType; newIdOrder: number[] } },
    ) => {
      const { type, newIdOrder } = action.payload;
      const group = state.sidepanelPropertyGroups.find(g => g.type === type);
      if (group) {
        const oldProperties = Array(...group.properties);
        const newGroup = newIdOrder.map(id => {
          return oldProperties.find(p => p.id === id)!;
        });

        state.sidepanelPropertyGroups = state.sidepanelPropertyGroups.map(g => {
          if (g.type === type) {
            return { ...g, properties: newGroup };
          }
          return g;
        });
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchGroups.fulfilled, (state, action) => {
      state.sidepanelPropertyGroups = action.payload;
    });

    builder.addCase(updateProperty.fulfilled, (state, action) => {
      const { id, ...payload } = action.payload;
      state.sidepanelPropertyGroups.forEach(group => {
        const property = group.properties.find(p => p.id === id);
        if (property) {
          Object.assign(property, payload);
        }
      });
    });
  },
});

export function initUiConfig(store: Store) {
  store.dispatch(fetchGroups());
}

export const selectSidepanelPropertyGroup = createSelector(
  (state: AppState) => state.uiConfig.sidepanelPropertyGroups,
  (_: AppState, type: SidepanelPropertyGroupType) => type,
  (groups, type) => groups.find(g => g.type === type),
);

export const selectSidepanelPropertiesByGroup = createSelector(
  (state: AppState) => state.uiConfig.sidepanelPropertyGroups,
  (_: AppState, type: SidepanelPropertyGroupType) => type,
  (groups, type) => groups.find(g => g.type === type)?.properties || [],
);

export const { reorderSidepanelProperties } = uiConfigSlice.actions;

export default uiConfigSlice.reducer;
